import posState from "./posState";
import posCart from "./posCart";
import posCatalog from "./posCatalog";
import posCheckout from "./posCheckout";
import posTransactionWorkspaces from "./posTransactionWorkspaces";

// ============================================================
// POS STORE FACADE
//
// Session and terminal lifecycle remain unchanged.
// Sales workspaces are identified by clientTransactionId.
// Existing component-facing methods remain available.
// ============================================================

const state = posState.state;

// ============================================================
// SALES TRANSACTION WORKSPACES
//
// Call initializeSalesTabs() only AFTER an active POS session
// has been loaded/started and its current transaction restored.
// Switching is blocked while payment/checkout is in progress.
// ============================================================

function initializeSalesTabs() {
  return posTransactionWorkspaces.initialize(state);
}

function saveActiveSalesTab() {
  return posTransactionWorkspaces.saveActive(state);
}

function openSalesTab() {
  return posTransactionWorkspaces.open(state);
}

function selectSalesTab(transactionId) {
  return posTransactionWorkspaces.select(state, transactionId);
}

function closeSalesTab(transactionId, options = {}) {
  return posTransactionWorkspaces.close(state, transactionId, options);
}

function clearSalesTabsAfterSessionEnd() {
  // Call only after successful existing backend session closure.
  posTransactionWorkspaces.clearAfterSessionEnd();
}

const posStore = {
  // ==========================================================
  // SHARED STATE
  // ==========================================================
  state,

  // ==========================================================
  // COMPUTED STATE
  // ==========================================================
  cartItemCount: posState.cartItemCount,
  cartSubtotal: posState.cartSubtotal,
  hasItems: posState.hasItems,
  hasSale: posState.hasSale,
  backendTotal: posState.backendTotal,
  outstandingAmount: posState.outstandingAmount,
  hasTerminal: posState.hasTerminal,
  hasActiveSession: posState.hasActiveSession,
  filteredProducts: posCatalog.filteredProducts,

  // ==========================================================
  // SALES TABS (ADDITIVE API)
  // ==========================================================
  salesTabs: posTransactionWorkspaces.registry.tabs,
  salesTabRegistry: posTransactionWorkspaces.registry,
  activeSalesTab: posTransactionWorkspaces.activeTab,
  canOpenSalesTab: posTransactionWorkspaces.canOpenTab,
  maxSalesTabs: posTransactionWorkspaces.MAX_TABS,
  initializeSalesTabs,
  saveActiveSalesTab,
  openSalesTab,
  selectSalesTab,
  closeSalesTab,
  clearSalesTabsAfterSessionEnd,

  // ==========================================================
  // CART
  // ==========================================================
  addProduct: posCart.addProduct,
  increaseQuantity: posCart.increaseQuantity,
  decreaseQuantity: posCart.decreaseQuantity,
  setQuantity: posCart.setQuantity,
  removeItem: posCart.removeItem,
  clearCart: posCart.clearCart,
  forceClearCart: posCart.forceClearCart,

  // ==========================================================
  // CATALOG
  // ==========================================================
  setProducts: posCatalog.setProducts,
  setCategories: posCatalog.setCategories,
  selectCategory: posCatalog.selectCategory,
  setSearchText: posCatalog.setSearchText,
  clearSearch: posCatalog.clearSearch,

  // ==========================================================
  // CURRENCY
  // ==========================================================
  setCurrency: posCheckout.setCurrency,

  // ==========================================================
  // DISCOUNT
  // ==========================================================
  setDiscountRate: posCheckout.setDiscountRate,
  setDiscountAmount: posCheckout.setDiscountAmount,

  // ==========================================================
  // TAX
  // ==========================================================
  setTaxCodes: posCheckout.setTaxCodes,
  setTaxCode: posCheckout.setTaxCode,

  // ==========================================================
  // TIP
  // ==========================================================
  setTipRate: posCheckout.setTipRate,
  setTipAmount: posCheckout.setTipAmount,

  // ==========================================================
  // CUSTOMER
  // ==========================================================
  setCustomer: posCheckout.setCustomer,

  // LOYALTY MEMBERSHIP
  setMembership: posCheckout.setMembership,

  // LOYALTY PROGRAM
  setLoyaltyProgram: posCheckout.setLoyaltyProgram,

  // ==========================================================
  // SALE
  // ==========================================================
  buildSalePayload: posCheckout.buildSalePayload,
  applySale: posCheckout.applySale,
  getSaleTotals: posCheckout.getSaleTotals,

  // ==========================================================
  // CHECKOUT
  // ==========================================================
  applyCheckout: posCheckout.applyCheckout,
  canCheckout: posCheckout.canCheckout,
  openCheckout: posCheckout.openCheckout,
  closeCheckout: posCheckout.closeCheckout,

  // ==========================================================
  // PAYMENT
  // ==========================================================
  setPaymentMethod: posCheckout.setPaymentMethod,
  setPaymentTransaction: posCheckout.setPaymentTransaction,
  setPaymentStatus: posCheckout.setPaymentStatus,

  // ==========================================================
  // RESET (EXISTING BEHAVIOR UNCHANGED)
  // ==========================================================
  resetTransaction: posState.resetTransaction,
  resetSession: posState.resetSession,
  resetTerminal: posState.resetTerminal,
  resetAll: posState.resetAll
};

export default posStore;
